import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Check, Sparkles, Upload, FileText, ArrowRight, Download, ChevronsUpDown, Link2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  { value: "NA", label: "NA" },
  { value: "EME", label: "EME" },
  { value: "APA", label: "APA" },
  { value: "Global", label: "Global" },
];

export default function BuilderPage() {
  usePageTitle(null); // Home page - uses base title
  const navigate = useNavigate();
  
  // Core form data (dropdowns and base URL)
  const [formData, setFormData] = useState({
    base_url: "",
    source: "",
    medium: "",
    brand: "",
    region: "NA",
  });

  // Text inputs - separate to prevent re-render interference
  const [campaignInput, setCampaignInput] = useState("");
  const [contentInput, setContentInput] = useState("");
  const [termInput, setTermInput] = useState("");

  const [bulkUrls, setBulkUrls] = useState("");
  
  // Dealer Redirect specific state
  const [dealerRedirectBaseUrls, setDealerRedirectBaseUrls] = useState("");
  const [dealerDestinationPaths, setDealerDestinationPaths] = useState("");
  const [dealerResults, setDealerResults] = useState([]);
  const [dealerLoading, setDealerLoading] = useState(false);
  const [dealerPreviewUrl, setDealerPreviewUrl] = useState("");
  
  // Dealer ITG specific state (no base URL)
  const [itgDestinationPaths, setItgDestinationPaths] = useState("");
  const [itgResults, setItgResults] = useState([]);
  const [itgLoading, setItgLoading] = useState(false);
  const [itgPreviewUrl, setItgPreviewUrl] = useState("");
  
  const [sourceMediums, setSourceMediums] = useState([]);
  const [brands, setBrands] = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  const [availableMediums, setAvailableMediums] = useState([]);
  const [currentChannel, setCurrentChannel] = useState("");
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);
  const [showSuccessGuide, setShowSuccessGuide] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [activeBuilderTab, setActiveBuilderTab] = useState("single");

  // Dealer-specific settings
  const [dealerSourceMediums, setDealerSourceMediums] = useState([]);
  const [dealerBrands, setDealerBrands] = useState([]);
  const [dealerAvailableSources, setDealerAvailableSources] = useState([]);
  const [dealerAvailableMediums, setDealerAvailableMediums] = useState([]);
  const [dealerCurrentChannel, setDealerCurrentChannel] = useState("");

  useEffect(() => {
    fetchData();
    fetchDealerData();
  }, []);

  const fetchData = async () => {
    try {
      await axios.post(`${API}/seed`);
      
      const [smResponse, brandsResponse] = await Promise.all([
        axios.get(`${API}/settings/source-mediums?config_type=standard`),
        axios.get(`${API}/settings/brands?config_type=standard`),
      ]);

      setSourceMediums(smResponse.data);
      setBrands(brandsResponse.data);

      const sources = [...new Set(smResponse.data.map((sm) => sm.source))];
      setAvailableSources(sources);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchDealerData = async () => {
    try {
      const [smResponse, brandsResponse] = await Promise.all([
        axios.get(`${API}/settings/source-mediums?config_type=dealer`),
        axios.get(`${API}/settings/brands?config_type=dealer`),
      ]);

      setDealerSourceMediums(smResponse.data);
      setDealerBrands(brandsResponse.data);

      const sources = [...new Set(smResponse.data.map((sm) => sm.source))];
      setDealerAvailableSources(sources);
    } catch (error) {
      console.error("Error fetching dealer data:", error);
    }
  };

  useEffect(() => {
    if (activeBuilderTab === "dealer" || activeBuilderTab === "itg") {
      // Update dealer mediums when dealer source changes (for both dealer and ITG tabs)
      if (formData.source) {
        const mediums = dealerSourceMediums
          .filter((sm) => sm.source === formData.source)
          .map((sm) => ({ medium: sm.medium, channel: sm.channel }));
        setDealerAvailableMediums(mediums);
      } else {
        setDealerAvailableMediums([]);
        setDealerCurrentChannel("");
      }
    } else {
      // Standard tab logic
      if (formData.source) {
        const mediums = sourceMediums
          .filter((sm) => sm.source === formData.source)
          .map((sm) => ({ medium: sm.medium, channel: sm.channel }));
        setAvailableMediums(mediums);
        setFormData((prev) => ({ ...prev, medium: "" }));
        setCurrentChannel("");
      } else {
        setAvailableMediums([]);
        setCurrentChannel("");
      }
    }
  }, [formData.source, sourceMediums, dealerSourceMediums, activeBuilderTab]);

  useEffect(() => {
    if (activeBuilderTab === "dealer" || activeBuilderTab === "itg") {
      if (formData.source && formData.medium) {
        const match = dealerSourceMediums.find(
          (sm) => sm.source === formData.source && sm.medium === formData.medium
        );
        if (match) setDealerCurrentChannel(match.channel);
      }
    } else {
      if (formData.source && formData.medium) {
        const match = sourceMediums.find(
          (sm) => sm.source === formData.source && sm.medium === formData.medium
        );
        if (match) setCurrentChannel(match.channel);
      }
    }
  }, [formData.medium, formData.source, sourceMediums, dealerSourceMediums, activeBuilderTab]);

  // Format text: lowercase and replace spaces/dashes with underscores
  const formatText = (value) => {
    return value.toLowerCase().replace(/[\s-]+/g, "_");
  };

  // Get formatted campaign with brand prefix
  const getFormattedCampaign = useCallback(() => {
    const campaignFormatted = formatText(campaignInput);
    if (formData.brand && campaignFormatted) {
      const brandPrefix = formData.brand.split(" ").pop().toLowerCase();
      return `${brandPrefix}_${campaignFormatted}`;
    }
    return campaignFormatted;
  }, [campaignInput, formData.brand]);

  // Get formatted content
  const getFormattedContent = useCallback(() => {
    return formatText(contentInput);
  }, [contentInput]);

  // Get formatted term
  const getFormattedTerm = useCallback(() => {
    return formatText(termInput);
  }, [termInput]);

  // Update URL preview when relevant fields change
  useEffect(() => {
    const campaign = getFormattedCampaign();
    const content = getFormattedContent();
    const term = getFormattedTerm();
    
    if (formData.base_url && formData.source && formData.medium && campaign) {
      const base = formData.base_url.trim().replace(/\/$/, "");
      const separator = base.includes("?") ? "&" : "?";
      
      let params = [];
      params.push(`utm_source=${formData.source}`);
      params.push(`utm_medium=${formData.medium}`);
      params.push(`utm_campaign=${campaign}`);
      if (content) params.push(`utm_content=${content}`);
      if (term) params.push(`utm_term=${term}`);
      
      setGeneratedUrl(`${base}${separator}${params.join("&")}`);
    } else {
      setGeneratedUrl("");
    }
  }, [formData.base_url, formData.source, formData.medium, getFormattedCampaign, getFormattedContent, getFormattedTerm]);

  // Update dealer redirect preview when relevant fields change
  useEffect(() => {
    const campaign = getFormattedCampaign();
    const content = getFormattedContent();
    const term = getFormattedTerm();
    
    // Get first base URL and first destination path for preview
    const firstBaseUrl = dealerRedirectBaseUrls.split("\n").filter(u => u.trim())[0] || "";
    const firstPath = dealerDestinationPaths.split("\n").filter(p => p.trim())[0] || "";
    
    if (firstBaseUrl && firstPath && formData.source && formData.medium && campaign) {
      const base = firstBaseUrl.trim().replace(/\/$/, "");
      
      let params = [];
      params.push(`utm_source=${formData.source}`);
      params.push(`utm_medium=${formData.medium}`);
      params.push(`utm_campaign=${campaign}`);
      if (content) params.push(`utm_content=${content}`);
      if (term) params.push(`utm_term=${term}`);
      
      // Format: base?q=path?&utm_params
      setDealerPreviewUrl(`${base}?q=${firstPath.trim()}?&${params.join("&")}`);
    } else {
      setDealerPreviewUrl("");
    }
  }, [dealerRedirectBaseUrls, dealerDestinationPaths, formData.source, formData.medium, getFormattedCampaign, getFormattedContent, getFormattedTerm]);

  // Update ITG preview (no base URL)
  useEffect(() => {
    const campaign = getFormattedCampaign();
    const content = getFormattedContent();
    const term = getFormattedTerm();
    
    const firstPath = itgDestinationPaths.split("\n").filter(p => p.trim())[0] || "";
    
    if (firstPath && formData.source && formData.medium && campaign) {
      let params = [];
      params.push(`utm_source=${formData.source}`);
      params.push(`utm_medium=${formData.medium}`);
      params.push(`utm_campaign=${campaign}`);
      if (content) params.push(`utm_content=${content}`);
      if (term) params.push(`utm_term=${term}`);
      
      // Format: ?q=path?&utm_params (no base URL)
      setItgPreviewUrl(`?q=${firstPath.trim()}?&${params.join("&")}`);
    } else {
      setItgPreviewUrl("");
    }
  }, [itgDestinationPaths, formData.source, formData.medium, getFormattedCampaign, getFormattedContent, getFormattedTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const campaign = getFormattedCampaign();

    if (!formData.base_url || !formData.source || !formData.medium || !campaign || !formData.region) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/links`, {
        base_url: formData.base_url,
        source: formData.source,
        medium: formData.medium,
        campaign: campaign,
        content: getFormattedContent(),
        term: getFormattedTerm(),
        brand: formData.brand,
        channel: currentChannel,
        region: formData.region,
      });
      toast.success("UTM link created and saved!");
      setShowSuccessGuide(true);
      
      // Reset form
      setFormData({
        base_url: "",
        source: "",
        medium: "",
        brand: "",
        region: formData.region,
      });
      setCampaignInput("");
      setContentInput("");
      setTermInput("");
      setCurrentChannel("");
      setGeneratedUrl("");
    } catch (error) {
      toast.error("Failed to save link");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();

    const campaign = getFormattedCampaign();

    if (!bulkUrls.trim() || !formData.source || !formData.medium || !campaign || !formData.region) {
      toast.error("Please fill in all required fields and enter URLs");
      return;
    }

    const urls = bulkUrls.split("\n").filter(url => url.trim());
    if (urls.length === 0) {
      toast.error("Please enter at least one URL");
      return;
    }

    setBulkLoading(true);
    try {
      const response = await axios.post(`${API}/links/bulk`, {
        base_urls: urls,
        source: formData.source,
        medium: formData.medium,
        campaign: campaign,
        content: getFormattedContent(),
        term: getFormattedTerm(),
        brand: formData.brand,
        channel: currentChannel,
        region: formData.region,
      });
      
      setBulkResults(response.data);
      toast.success(`${response.data.length} UTM links created!`);
      setShowSuccessGuide(true);
      setBulkUrls("");
    } catch (error) {
      toast.error("Failed to create bulk links");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      setBulkUrls(text);
      toast.success(`Loaded ${text.split("\n").filter(l => l.trim()).length} URLs from file`);
    };
    reader.readAsText(file);
  };

  const copyToClipboard = async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      toast.success("URL copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const copyBulkResults = async () => {
    if (bulkResults.length === 0) return;
    const urls = bulkResults.map(r => r.generated_url).join("\n");
    try {
      await navigator.clipboard.writeText(urls);
      toast.success("All URLs copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleDealerSubmit = async (e) => {
    e.preventDefault();

    const campaign = getFormattedCampaign();

    const baseUrls = dealerRedirectBaseUrls.split("\n").filter(url => url.trim());
    const paths = dealerDestinationPaths.split("\n").filter(path => path.trim());

    if (baseUrls.length === 0 || paths.length === 0 || !formData.source || !formData.medium || !campaign || !formData.region) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (baseUrls.length !== paths.length) {
      toast.error(`Mismatch: ${baseUrls.length} Base URLs vs ${paths.length} Destination Pages. Counts must match.`);
      return;
    }

    setDealerLoading(true);
    try {
      const response = await axios.post(`${API}/dealer-links/bulk`, {
        redirect_base_urls: baseUrls,
        destination_paths: paths,
        source: formData.source,
        medium: formData.medium,
        campaign: campaign,
        content: getFormattedContent(),
        term: getFormattedTerm(),
        brand: formData.brand,
        channel: dealerCurrentChannel,
        region: formData.region,
      });
      
      setDealerResults(response.data);
      toast.success(`${response.data.length} dealer redirect link(s) created!`);
      setShowSuccessGuide(true);
      setDealerRedirectBaseUrls("");
      setDealerDestinationPaths("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create dealer redirect links");
    } finally {
      setDealerLoading(false);
    }
  };

  const copyDealerResults = async () => {
    if (dealerResults.length === 0) return;
    const urls = dealerResults.map(r => r.generated_url).join("\n");
    try {
      await navigator.clipboard.writeText(urls);
      toast.success("All URLs copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  // ITG Submit Handler (no base URL, save to DB)
  const handleItgSubmit = async (e) => {
    e.preventDefault();
    const campaign = getFormattedCampaign();
    const paths = itgDestinationPaths.split("\n").filter(path => path.trim());

    if (paths.length === 0 || !formData.source || !formData.medium || !campaign || !formData.region) {
      toast.error("Please fill in all required fields");
      return;
    }

    setItgLoading(true);
    try {
      const response = await axios.post(`${API}/itg-links/bulk`, {
        destination_paths: paths,
        source: formData.source,
        medium: formData.medium,
        campaign: campaign,
        content: getFormattedContent(),
        term: getFormattedTerm(),
        brand: formData.brand,
        channel: dealerCurrentChannel,
        region: formData.region,
      });
      
      setItgResults(response.data);
      toast.success(`${response.data.length} ITG link(s) created!`);
      setShowSuccessGuide(true);
      setItgDestinationPaths("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create ITG links");
    } finally {
      setItgLoading(false);
    }
  };

  const copyItgResults = async () => {
    if (itgResults.length === 0) return;
    const urls = itgResults.map(r => r.generated_url).join("\n");
    try {
      await navigator.clipboard.writeText(urls);
      toast.success("All URLs copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  // Dealer-specific form fields
  const renderDealerFormFields = () => (
    <>
      {/* Region Selector */}
      <div>
        <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
          Region *
        </label>
        <RadioGroup
          value={formData.region}
          onValueChange={(value) => setFormData((prev) => ({ ...prev, region: value }))}
          className="flex gap-6"
          data-testid="dealer-region-selector"
        >
          {REGIONS.map((region) => (
            <div key={region.value} className="flex items-center space-x-2">
              <RadioGroupItem 
                value={region.value} 
                id={`dealer-region-${region.value}`}
                className="border-2 border-zinc-300 text-[#AE132A] focus:ring-[#AE132A]"
              />
              <Label htmlFor={`dealer-region-${region.value}`} className="text-sm font-medium cursor-pointer">
                {region.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Dealer Source & Medium */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
            Source *
          </label>
          <Select
            value={formData.source}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, source: value, medium: "" }))}
          >
            <SelectTrigger className="rounded-none h-12 border-0 border-b-2 border-zinc-200 focus:border-[#AE132A]" data-testid="dealer-source-select">
              <SelectValue placeholder="Select source" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {[...dealerAvailableSources].sort((a, b) => a.localeCompare(b)).map((source) => (
                <SelectItem key={source} value={source} className="rounded-none">{source}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
            Medium *
          </label>
          <Select
            value={formData.medium}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, medium: value }))}
            disabled={!formData.source}
          >
            <SelectTrigger className="rounded-none h-12 border-0 border-b-2 border-zinc-200 focus:border-[#AE132A]" data-testid="dealer-medium-select">
              <SelectValue placeholder={formData.source ? "Select medium" : "Select source first"} />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {dealerAvailableMediums.map((item) => (
                <SelectItem key={item.medium} value={item.medium} className="rounded-none">{item.medium}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* GA4 Channel */}
      {dealerCurrentChannel && (
        <div className="animate-slide-in">
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">GA4 Channel Group</label>
          <div className="h-12 flex items-center px-4 bg-zinc-100 border-l-2 border-[#AE132A]">
            <span className="text-sm font-medium text-zinc-700">{dealerCurrentChannel}</span>
          </div>
        </div>
      )}

      {/* Campaign */}
      <div>
        <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">Campaign Name *</label>
        <input
          type="text" value={campaignInput} onChange={(e) => setCampaignInput(e.target.value)}
          placeholder="winter_sale"
          className="w-full bg-transparent border-0 border-b-2 border-zinc-200 focus:border-[#AE132A] focus:outline-none focus:ring-0 h-12 font-mono text-sm"
          data-testid="dealer-campaign-input" autoComplete="off"
        />
        <p className="text-xs text-zinc-400 mt-2">Final: <span className="font-mono text-zinc-600">{getFormattedCampaign() || "brand_campaign_name"}</span></p>
      </div>

      {/* Dealer Brand */}
      <div>
        <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">Brand (Optional)</label>
        <Select value={formData.brand || "none"} onValueChange={(value) => setFormData((prev) => ({ ...prev, brand: value === "none" ? "" : value }))}>
          <SelectTrigger className="rounded-none h-12 border-0 border-b-2 border-zinc-200 focus:border-[#AE132A]" data-testid="dealer-brand-select">
            <SelectValue placeholder="Select brand (optional)" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="none" className="rounded-none text-zinc-400">No brand</SelectItem>
            {dealerBrands.map((brand) => (
              <SelectItem key={brand.id} value={brand.name} className="rounded-none">{brand.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Content & Term */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">UTM Content</label>
          <input type="text" value={contentInput} onChange={(e) => setContentInput(e.target.value)} placeholder="banner_v1"
            className="w-full bg-transparent border-0 border-b-2 border-zinc-200 focus:border-[#AE132A] focus:outline-none focus:ring-0 h-12 text-sm"
            data-testid="dealer-content-input" autoComplete="off" />
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">UTM Term</label>
          <input type="text" value={termInput} onChange={(e) => setTermInput(e.target.value)} placeholder="running_shoes"
            className="w-full bg-transparent border-0 border-b-2 border-zinc-200 focus:border-[#AE132A] focus:outline-none focus:ring-0 h-12 text-sm"
            data-testid="dealer-term-input" autoComplete="off" />
        </div>
      </div>
    </>
  );

  // Common form fields rendered inline (not as a component to preserve input state)
  const renderFormFields = () => (
    <>
      {/* Region Selector */}
      <div>
        <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
          Region *
        </label>
        <RadioGroup
          value={formData.region}
          onValueChange={(value) => setFormData((prev) => ({ ...prev, region: value }))}
          className="flex gap-6"
          data-testid="region-selector"
        >
          {REGIONS.map((region) => (
            <div key={region.value} className="flex items-center space-x-2">
              <RadioGroupItem 
                value={region.value} 
                id={`region-${region.value}`}
                className="border-2 border-zinc-300 text-[#AE132A] focus:ring-[#AE132A]"
              />
              <Label 
                htmlFor={`region-${region.value}`}
                className="text-sm font-medium cursor-pointer"
              >
                {region.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Source & Medium */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
            Source *
          </label>
          <Popover open={sourceOpen} onOpenChange={setSourceOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={sourceOpen}
                className="w-full justify-between rounded-none h-12 border-0 border-b-2 border-zinc-200 hover:border-zinc-300 focus:border-[#AE132A] bg-transparent font-normal"
                data-testid="source-select"
              >
                {formData.source || "Select source"}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0 rounded-none">
              <Command>
                <CommandInput placeholder="Search source..." className="h-10" />
                <CommandList>
                  <CommandEmpty>No source found.</CommandEmpty>
                  <CommandGroup>
                    {[...availableSources].sort((a, b) => a.localeCompare(b)).map((source) => (
                      <CommandItem
                        key={source}
                        value={source}
                        onSelect={(currentValue) => {
                          setFormData((prev) => ({ ...prev, source: currentValue }));
                          setSourceOpen(false);
                        }}
                        className="rounded-none"
                      >
                        <Check
                          className={`mr-2 h-4 w-4 ${formData.source === source ? "opacity-100" : "opacity-0"}`}
                        />
                        {source}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
            Medium *
          </label>
          <Select
            value={formData.medium}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, medium: value }))}
            disabled={!formData.source}
          >
            <SelectTrigger className="rounded-none h-12 border-0 border-b-2 border-zinc-200 focus:border-[#AE132A]" data-testid="medium-select">
              <SelectValue placeholder={formData.source ? "Select medium" : "Select source first"} />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {availableMediums.map((item) => (
                <SelectItem key={item.medium} value={item.medium} className="rounded-none">
                  {item.medium}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* GA4 Channel - Auto Display */}
      {currentChannel && (
        <div className="animate-slide-in">
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
            GA4 Channel Group
          </label>
          <div className="h-12 flex items-center px-4 bg-zinc-100 border-l-2 border-[#AE132A]">
            <span className="text-sm font-medium text-zinc-700" data-testid="channel-display">
              {currentChannel}
            </span>
          </div>
        </div>
      )}

      {/* Campaign - Native input to prevent cursor issues */}
      <div>
        <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
          Campaign Name *
        </label>
        <input
          type="text"
          value={campaignInput}
          onChange={(e) => setCampaignInput(e.target.value)}
          placeholder="winter_sale"
          className="w-full bg-transparent border-0 border-b-2 border-zinc-200 focus:border-[#AE132A] focus:outline-none focus:ring-0 h-12 font-mono text-sm"
          data-testid="campaign-input"
          autoComplete="off"
        />
        <p className="text-xs text-zinc-400 mt-2">
          Final: <span className="font-mono text-zinc-600">{getFormattedCampaign() || "brand_campaign_name"}</span>
        </p>
      </div>

      {/* Brand */}
      <div>
        <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
          Brand (Optional)
        </label>
        <Select
          value={formData.brand || "none"}
          onValueChange={(value) => setFormData((prev) => ({ ...prev, brand: value === "none" ? "" : value }))}
        >
          <SelectTrigger className="rounded-none h-12 border-0 border-b-2 border-zinc-200 focus:border-[#AE132A]" data-testid="brand-select">
            <SelectValue placeholder="Select brand (optional)" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="none" className="rounded-none text-zinc-400">
              No brand
            </SelectItem>
            {brands.map((brand) => (
              <SelectItem key={brand.id} value={brand.name} className="rounded-none">
                {brand.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Content & Term - Native inputs */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
            UTM Content
          </label>
          <input
            type="text"
            value={contentInput}
            onChange={(e) => setContentInput(e.target.value)}
            placeholder="banner_v1"
            className="w-full bg-transparent border-0 border-b-2 border-zinc-200 focus:border-[#AE132A] focus:outline-none focus:ring-0 h-12 text-sm"
            data-testid="content-input"
            autoComplete="off"
          />
          {contentInput && contentInput !== getFormattedContent() && (
            <p className="text-xs text-zinc-400 mt-1">
              Final: <span className="font-mono text-zinc-600">{getFormattedContent()}</span>
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
            UTM Term
          </label>
          <input
            type="text"
            value={termInput}
            onChange={(e) => setTermInput(e.target.value)}
            placeholder="running_shoes"
            className="w-full bg-transparent border-0 border-b-2 border-zinc-200 focus:border-[#AE132A] focus:outline-none focus:ring-0 h-12 text-sm"
            data-testid="term-input"
            autoComplete="off"
          />
          {termInput && termInput !== getFormattedTerm() && (
            <p className="text-xs text-zinc-400 mt-1">
              Final: <span className="font-mono text-zinc-600">{getFormattedTerm()}</span>
            </p>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="p-8" data-testid="builder-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-zinc-950 red-thread pb-3">
          UTM Builder
        </h1>
        <p className="text-sm text-zinc-500 mt-4">
          Generate standardized UTM links for your marketing campaigns
        </p>
      </div>

      {/* Tabs for Single/Bulk */}
      <Tabs value={activeBuilderTab} onValueChange={(val) => { setActiveBuilderTab(val); setFormData(prev => ({...prev, source: "", medium: ""})); setCurrentChannel(""); setDealerCurrentChannel(""); }} className="space-y-6">
        <TabsList className="bg-zinc-100 p-1 rounded-none">
          <TabsTrigger
            value="single"
            className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#AE132A] uppercase text-xs tracking-widest font-medium px-6"
            data-testid="single-link-tab"
          >
            <FileText className="w-4 h-4 mr-2" />
            Single Link
          </TabsTrigger>
          <TabsTrigger
            value="bulk"
            className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#AE132A] uppercase text-xs tracking-widest font-medium px-6"
            data-testid="bulk-link-tab"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Creation
          </TabsTrigger>
          <TabsTrigger
            value="dealer"
            className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#AE132A] uppercase text-xs tracking-widest font-medium px-6"
            data-testid="dealer-redirect-tab"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Dealer Redirect
          </TabsTrigger>
          <TabsTrigger
            value="itg"
            className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#AE132A] uppercase text-xs tracking-widest font-medium px-6"
            data-testid="dealer-itg-tab"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Dealer Redirect ITG
          </TabsTrigger>
        </TabsList>

        {/* Single Link Tab */}
        <TabsContent value="single">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left - Form */}
            <div className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Base URL */}
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
                    Base URL *
                  </label>
                  <Input
                    type="url"
                    value={formData.base_url}
                    onChange={(e) => setFormData((prev) => ({ ...prev, base_url: e.target.value }))}
                    placeholder="https://example.com/page"
                    className="input-underline"
                    data-testid="base-url-input"
                  />
                </div>

                {renderFormFields()}

                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="generate-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  {loading ? "Saving..." : "Generate & Save Link"}
                </Button>
              </form>
            </div>

            {/* Right - Preview */}
            <div className="lg:sticky lg:top-8 space-y-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
                  Generated URL Preview
                </label>
                <div className="url-preview min-h-[120px] relative" data-testid="url-preview">
                  {generatedUrl ? (
                    <span>{generatedUrl}</span>
                  ) : (
                    <span className="text-zinc-500">
                      Fill in the form to generate your UTM link...
                    </span>
                  )}
                </div>
              </div>

              <Button
                type="button"
                onClick={copyToClipboard}
                disabled={!generatedUrl}
                className="btn-secondary w-full flex items-center justify-center gap-2"
                data-testid="copy-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy to Clipboard
                  </>
                )}
              </Button>

              {/* Quick Info */}
              <div className="border border-zinc-200 p-6 mt-6">
                <h3 className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-4">
                  UTM Parameters
                </h3>
                <ul className="space-y-2 text-sm text-zinc-600">
                  <li className="flex items-start gap-2">
                    <span className="text-[#AE132A] font-bold">•</span>
                    <span><strong>Source:</strong> Where traffic originates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#AE132A] font-bold">•</span>
                    <span><strong>Medium:</strong> Marketing medium type</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#AE132A] font-bold">•</span>
                    <span><strong>Campaign:</strong> Specific campaign name</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#AE132A] font-bold">•</span>
                    <span><strong>Region:</strong> Geographic target area</span>
                  </li>
                </ul>
              </div>

              {/* Success Guide */}
              {showSuccessGuide && (
                <div className="border-l-4 border-[#AE132A] bg-[#AE132A]/5 p-4 animate-slide-in" data-testid="success-guide">
                  <div className="flex items-start gap-3">
                    <Download className="w-5 h-5 text-[#AE132A] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-zinc-800 mb-2">
                        Link saved successfully!
                      </p>
                      <p className="text-xs text-zinc-600 mb-3">
                        You can download all your generated links from the Link Repository page. Select the links you need and export them to Excel/CSV.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate("/archive")}
                        className="rounded-none text-xs h-8 border-[#AE132A] text-[#AE132A] hover:bg-[#AE132A] hover:text-white"
                        data-testid="go-to-repository-btn"
                      >
                        Go to Link Repository
                        <ArrowRight className="w-3 h-3 ml-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Bulk Link Tab */}
        <TabsContent value="bulk">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left - Form */}
            <div className="space-y-6">
              <form onSubmit={handleBulkSubmit} className="space-y-6">
                {/* URLs Input */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500">
                      Base URLs (one per line) *
                    </label>
                  </div>
                  <Textarea
                    value={bulkUrls}
                    onChange={(e) => setBulkUrls(e.target.value)}
                    placeholder="https://example.com/page1&#10;https://example.com/page2&#10;https://example.com/page3"
                    className="min-h-[150px] font-mono text-sm rounded-none border-zinc-200 focus:border-[#AE132A] focus:ring-0"
                    data-testid="bulk-urls-input"
                  />
                  <p className="text-xs text-zinc-400 mt-2">
                    {bulkUrls.split("\n").filter(l => l.trim()).length} URLs entered
                  </p>
                </div>

                {renderFormFields()}

                <Button
                  type="submit"
                  disabled={bulkLoading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="bulk-generate-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  {bulkLoading ? "Creating..." : "Generate All Links"}
                </Button>
              </form>
            </div>

            {/* Right - Results */}
            <div className="lg:sticky lg:top-8 space-y-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
                  Bulk Results ({bulkResults.length} links)
                </label>
                <div className="url-preview min-h-[300px] max-h-[400px] overflow-y-auto" data-testid="bulk-results">
                  {bulkResults.length > 0 ? (
                    <div className="space-y-2">
                      {bulkResults.map((result, index) => (
                        <div key={result.id} className="text-xs">
                          <span className="text-zinc-500">{index + 1}.</span>{" "}
                          {result.generated_url}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-zinc-500">
                      Generated URLs will appear here...
                    </span>
                  )}
                </div>
              </div>

              {bulkResults.length > 0 && (
                <Button
                  type="button"
                  onClick={copyBulkResults}
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                  data-testid="copy-bulk-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copy All URLs
                </Button>
              )}

              {/* Success Guide for Bulk */}
              {showSuccessGuide && bulkResults.length > 0 && (
                <div className="border-l-4 border-[#AE132A] bg-[#AE132A]/5 p-4 animate-slide-in" data-testid="bulk-success-guide">
                  <div className="flex items-start gap-3">
                    <Download className="w-5 h-5 text-[#AE132A] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-zinc-800 mb-2">
                        {bulkResults.length} links saved successfully!
                      </p>
                      <p className="text-xs text-zinc-600 mb-3">
                        You can download all your generated links from the Link Repository page. Select the links you need and export them to Excel/CSV.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate("/archive")}
                        className="rounded-none text-xs h-8 border-[#AE132A] text-[#AE132A] hover:bg-[#AE132A] hover:text-white"
                        data-testid="bulk-go-to-repository-btn"
                      >
                        Go to Link Repository
                        <ArrowRight className="w-3 h-3 ml-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Dealer Redirect Tab */}
        <TabsContent value="dealer">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left - Form */}
            <div className="space-y-6">
              <form onSubmit={handleDealerSubmit} className="space-y-6">
                {/* Info Banner */}
                <div className="border-l-4 border-[#AE132A] bg-zinc-50 p-4">
                  <p className="text-xs text-zinc-600">
                    <strong>Multi-Country Dealer Links:</strong> Enter Base URLs and Destination Pages in matching order (1:1 pairing).<br />
                    <code className="text-[#AE132A] bg-zinc-100 px-1 py-0.5 mt-1 inline-block">
                      [Base URL]?q=[Destination]?&amp;utm_source=...
                    </code>
                  </p>
                </div>

                {/* Redirect Base URLs - Now a textarea */}
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
                    Redirect Base URLs (one per line) *
                  </label>
                  <Textarea
                    value={dealerRedirectBaseUrls}
                    onChange={(e) => setDealerRedirectBaseUrls(e.target.value)}
                    placeholder="https://parts.agcocorp.com/us/en/dealers/redirect/lowe&young&#10;https://parts.agcocorp.com/gb/en/dealers/redirect/agwood"
                    className="min-h-[100px] font-mono text-sm rounded-none border-zinc-200 focus:border-[#AE132A] focus:ring-0"
                    data-testid="dealer-redirect-base-urls"
                  />
                  <p className="text-xs text-zinc-400 mt-2">
                    {dealerRedirectBaseUrls.split("\n").filter(l => l.trim()).length} base URL(s) entered
                  </p>
                </div>

                {/* Destination Paths */}
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
                    Destination Pages (one per line, same order) *
                  </label>
                  <Textarea
                    value={dealerDestinationPaths}
                    onChange={(e) => setDealerDestinationPaths(e.target.value)}
                    placeholder="/c/filter-em1100&#10;/"
                    className="min-h-[100px] font-mono text-sm rounded-none border-zinc-200 focus:border-[#AE132A] focus:ring-0"
                    data-testid="dealer-destination-paths"
                  />
                  {(() => {
                    const urlCount = dealerRedirectBaseUrls.split("\n").filter(l => l.trim()).length;
                    const pathCount = dealerDestinationPaths.split("\n").filter(l => l.trim()).length;
                    const isMatch = urlCount === pathCount && urlCount > 0;
                    return (
                      <p className={`text-xs mt-2 ${isMatch ? 'text-green-600' : 'text-zinc-400'}`}>
                        {pathCount} path(s) entered {urlCount > 0 && pathCount > 0 && (isMatch ? '✓ Counts match' : `⚠ Mismatch with ${urlCount} URLs`)}
                      </p>
                    );
                  })()}
                </div>

                {renderDealerFormFields()}

                <Button
                  type="submit"
                  disabled={dealerLoading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="dealer-generate-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  {dealerLoading ? "Creating..." : "Generate Dealer Links"}
                </Button>
              </form>
            </div>

            {/* Right - Preview & Results */}
            <div className="lg:sticky lg:top-8 space-y-4">
              {/* Preview */}
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
                  URL Preview (First Link)
                </label>
                <div className="url-preview min-h-[80px]" data-testid="dealer-url-preview">
                  {dealerPreviewUrl ? (
                    <span className="break-all">{dealerPreviewUrl}</span>
                  ) : (
                    <span className="text-zinc-500">
                      Fill in the form to preview your dealer redirect URL...
                    </span>
                  )}
                </div>
              </div>

              {/* Results */}
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
                  Generated Links ({dealerResults.length})
                </label>
                <div className="url-preview min-h-[200px] max-h-[350px] overflow-y-auto" data-testid="dealer-results">
                  {dealerResults.length > 0 ? (
                    <div className="space-y-2">
                      {dealerResults.map((result, index) => (
                        <div key={result.id} className="text-xs">
                          <span className="text-zinc-500">{index + 1}.</span>{" "}
                          <span className="break-all">{result.generated_url}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-zinc-500">
                      Generated dealer redirect URLs will appear here...
                    </span>
                  )}
                </div>
              </div>

              {dealerResults.length > 0 && (
                <Button
                  type="button"
                  onClick={copyDealerResults}
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                  data-testid="copy-dealer-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copy All URLs
                </Button>
              )}

              {/* Success Guide for Dealer */}
              {showSuccessGuide && dealerResults.length > 0 && (
                <div className="border-l-4 border-[#AE132A] bg-[#AE132A]/5 p-4 animate-slide-in" data-testid="dealer-success-guide">
                  <div className="flex items-start gap-3">
                    <Download className="w-5 h-5 text-[#AE132A] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-zinc-800 mb-2">
                        {dealerResults.length} dealer link(s) saved!
                      </p>
                      <p className="text-xs text-zinc-600 mb-3">
                        View and export your dealer redirect links from the Link Repository page under the &quot;Dealer Redirect&quot; tab.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate("/archive")}
                        className="rounded-none text-xs h-8 border-[#AE132A] text-[#AE132A] hover:bg-[#AE132A] hover:text-white"
                        data-testid="dealer-go-to-repository-btn"
                      >
                        Go to Link Repository
                        <ArrowRight className="w-3 h-3 ml-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Dealer Redirect ITG Tab (No Base URL) */}
        <TabsContent value="itg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left - Form */}
            <div className="space-y-6">
              <form onSubmit={handleItgSubmit} className="space-y-6">
                {/* Info Banner */}
                <div className="border-l-4 border-[#AE132A] bg-zinc-50 p-4">
                  <p className="text-xs text-zinc-600">
                    <strong>ITG Dealer Links (No Base URL):</strong> Enter only Destination Pages. Output format:<br />
                    <code className="text-[#AE132A] bg-zinc-100 px-1 py-0.5 mt-1 inline-block">
                      ?q=[Destination]?&amp;utm_source=...
                    </code>
                  </p>
                </div>

                {/* Destination Paths Only */}
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
                    Destination Pages (one per line) *
                  </label>
                  <Textarea
                    value={itgDestinationPaths}
                    onChange={(e) => setItgDestinationPaths(e.target.value)}
                    placeholder="/c/filter-em1100&#10;/"
                    className="min-h-[120px] font-mono text-sm rounded-none border-zinc-200 focus:border-[#AE132A] focus:ring-0"
                    data-testid="itg-destination-paths"
                  />
                  <p className="text-xs text-zinc-400 mt-2">
                    {itgDestinationPaths.split("\n").filter(l => l.trim()).length} destination path(s) entered
                  </p>
                </div>

                {renderDealerFormFields()}

                <Button
                  type="submit"
                  disabled={itgLoading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="itg-generate-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  {itgLoading ? "Generating..." : "Generate ITG Links"}
                </Button>
              </form>
            </div>

            {/* Right - Preview & Results */}
            <div className="lg:sticky lg:top-8 space-y-4">
              {/* Preview */}
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
                  URL Preview (First Link)
                </label>
                <div className="url-preview min-h-[80px]" data-testid="itg-url-preview">
                  {itgPreviewUrl ? (
                    <span className="break-all">{itgPreviewUrl}</span>
                  ) : (
                    <span className="text-zinc-500">
                      Fill in the form to preview your ITG link...
                    </span>
                  )}
                </div>
              </div>

              {/* Results */}
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">
                  Generated Links ({itgResults.length})
                </label>
                <div className="url-preview min-h-[200px] max-h-[350px] overflow-y-auto" data-testid="itg-results">
                  {itgResults.length > 0 ? (
                    <div className="space-y-2">
                      {itgResults.map((result, index) => (
                        <div key={index} className="text-xs">
                          <span className="text-zinc-500">{index + 1}.</span>{" "}
                          <span className="break-all">{result.generated_url}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-zinc-500">
                      Generated ITG URLs will appear here...
                    </span>
                  )}
                </div>
              </div>

              {itgResults.length > 0 && (
                <Button
                  type="button"
                  onClick={copyItgResults}
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                  data-testid="copy-itg-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copy All URLs
                </Button>
              )}

              {/* Success Guide for ITG */}
              {showSuccessGuide && itgResults.length > 0 && (
                <div className="border-l-4 border-[#AE132A] bg-[#AE132A]/5 p-4 animate-slide-in" data-testid="itg-success-guide">
                  <div className="flex items-start gap-3">
                    <Download className="w-5 h-5 text-[#AE132A] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-zinc-800 mb-2">
                        {itgResults.length} ITG link(s) saved!
                      </p>
                      <p className="text-xs text-zinc-600 mb-3">
                        View and export your ITG links from the Link Repository page under the &quot;Dealer Redirect ITG&quot; tab.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate("/archive")}
                        className="rounded-none text-xs h-8 border-[#AE132A] text-[#AE132A] hover:bg-[#AE132A] hover:text-white"
                        data-testid="itg-go-to-repository-btn"
                      >
                        Go to Link Repository
                        <ArrowRight className="w-3 h-3 ml-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
